import { createFileRoute, Outlet } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { useCurrentUser } from '~/hooks/useCurrentUser';

/**
 * The Follow-Up Tutorials shell: the archive and the single post.
 *
 * Legacy's gate is an allowlist of follow-up partners, coordinators and admins.
 * It's the opposite of the study helper tutorials blocklist and the two
 * audiences don't overlap, so the server keeps two separate requirements; don't
 * unify them.
 *
 * The server refuses others with 403 (401 when signed out) where legacy
 * returned 404, so this shell renders the not-found screen. Defence in depth,
 * never the only gate.
 */
function FollowUpTutorialsLayout(): ReactElement | null {
  const { me, isPending } = useCurrentUser();

  // `me.profile_type`, not `useCurrentUser().isStudyHelper`, which would admit
  // the study helpers both endpoints refuse.
  const allowed =
    me?.profile_type === 'FOLLOWUP_COORDINATOR' ||
    me?.profile_type === 'FOLLOWUP_PARTNER' ||
    // Admins pass the server's requirement, so a gate without them would render
    // NotFoundScreen over a working response. There's no admin nav item for
    // this.
    me?.profile_type === 'ADMIN';

  // `/users/me` is pending on first paint, and a bare `!allowed` bounces every
  // caller, coordinators included.
  if (isPending) return null;
  if (!allowed) return <NotFoundScreen />;

  return <Outlet />;
}

export const Route = createFileRoute('/_app/follow-up-tutorials')({
  component: FollowUpTutorialsLayout,
});
